<script setup lang="ts">
import {
    EntityNameSpan,
    NamedNode,
} from '@precon-tools-suite-by-shared/shared-names-ui';
import Skeleton from 'primevue/skeleton';
import { useI18n } from 'vue-i18n';

export type RiskNameSpanProps = {
    threat?: NamedNode | null;
    productCategories?: {
        totalCount?: number | null;
        pageInfo: {
            hasNextPage: boolean;
            endCursor?: string | null;
        };
        nodes?: Array<NamedNode | null | undefined> | null;
    } | null;
    trashedProductCategories?: {
        totalCount?: number | null;
        pageInfo: {
            hasNextPage: boolean;
            endCursor?: string | null;
        };
        nodes?: Array<NamedNode | null | undefined> | null;
    } | null;
    size?: 'auto' | 'full' | 'short';
    loading?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = withDefaults(defineProps<RiskNameSpanProps>(), {
    threat: undefined,
    productCategories: undefined,
    trashedProductCategories: undefined,
    size: 'auto',
    loading: false,
});

const { t } = useI18n();
const seperator = ', ';
</script>
<template>
    <Skeleton v-if="loading" style="min-width: 18rem; min-height: 2rem" />
    <span v-else>
        <EntityNameSpan :node="threat" />

        <!-- Add space between threat name and 'in' -->
        {{ ' ' }}

        <!-- Active product categories -->
        <template
            v-if="productCategories && (productCategories.totalCount ?? 0) > 0"
        >
            {{ t('in') }}
            <span>
                <!-- Iterate through categories and add comma if not the last one -->
                <template
                    v-for="(category, index) in productCategories.nodes"
                    :key="category?.name?.full"
                >
                    <EntityNameSpan :node="category" :size="size" />
                    <span
                        v-if="
                            index < (productCategories.nodes?.length ?? 0) - 1
                        "
                        >{{ seperator }}
                    </span>
                </template>
            </span>

            <!-- Show 'and more' if there are more categories than shown -->
            <span v-if="productCategories.pageInfo.hasNextPage">{{
                t('andMore')
            }}</span>
        </template>

        <!-- Deleted product categories -->
        <template
            v-else-if="
                trashedProductCategories &&
                (trashedProductCategories.totalCount ?? 0) > 0
            "
        >
            <i
                v-tooltip.bottom="{
                    value: t('trashedExplanation'),
                    pt: {
                        text: {
                            style: {
                                minWidth: '600px', // Adjust the width to your liking
                                whiteSpace: 'normal', // This will allow wrapping for long text
                            },
                        },
                    },
                }"
                class="pi pi-info-circle text-info"
            />
            {{ t('deletedCategories') }}

            <span>
                <template
                    v-for="(category, index) in trashedProductCategories.nodes"
                    :key="category?.name?.full"
                >
                    <EntityNameSpan :node="category" :size="size" />
                    <span
                        v-if="
                            index <
                            (trashedProductCategories.nodes?.length ?? 0) - 1
                        "
                        >{{ seperator }}
                    </span>
                </template>
            </span>

            <!-- Show 'and more' for deleted categories if applicable -->
            <span v-if="trashedProductCategories.pageInfo.hasNextPage">{{
                t('andMore')
            }}</span>
        </template>

        <!-- Fallback message -->
        <template v-else>
            <i v-tooltip="t('noCategories')" class="pi pi-exclamation-circle" />
        </template>
    </span>
</template>
<i18n>
{
    "en": {
    "in": "in",
    "andMore": ", and more...",
    "deletedCategories": "in (now deleted categories):",
    "noCategories": "this threat is not (or was not) associated with a product category you are allowed to see",
    "trashedExplanation": "A risk represents a threat (severity) in one or more product categories (probability). When product categories are trashed, the risk can no longer be assigned to new products, as those categories are no longer available. However, the risk may still be relevant for older products, and we maintain the connection to trashed categories for backward compatibility."
    },
    "nl": {
    "in": "in",
    "andMore": ", en meer...",
    "deletedCategories": "in (nu verwijderde categorieën):",
    "noCategories": "dit gevaar is verbonden (geweest) aan een productCategorie die u niet (meer) mag zien",
    "trashedExplanation": "Een risico vertegenwoordigt een bedreiging (ernst) in een of meer productcategorieën (kans). Wanneer productcategorieën worden verwijderd, kan het risico niet langer worden toegewezen aan nieuwe producten, omdat die categorieën niet meer beschikbaar zijn. Het risico kan echter nog steeds relevant zijn voor oudere producten, en we behouden de verbinding met verwijderde categorieën voor compatibiliteit met eerdere producten."
    },
    "fr": {
    "in": "dans",
    "andMore": ", et plus...",
    "deletedCategories": "dans (catégories supprimées):",
    "noCategories": "cette menace n'est pas (ou n'a pas été) associée à une catégorie de produit que vous êtes autorisé à voir",
    "trashedExplanation": "Un risque représente une menace (gravité) dans une ou plusieurs catégories de produits (probabilité). Lorsque des catégories de produits sont supprimées, le risque ne peut plus être attribué à de nouveaux produits, car ces catégories ne sont plus disponibles. Cependant, le risque peut toujours être pertinent pour les anciens produits, et nous maintenons la connexion aux catégories supprimées pour la compatibilité avec les produits plus anciens."
    }
}
</i18n>
